import { PriceSlideFacet } from '@process-creative/slate-ajax-collection';
import { printMoney } from '@process-creative/slate-theme-tools';
import sliderConfig, { SliderUtils, PRICE_DISPLAY_CONFIG } from '../../../config/sliderConfig';

export const ATTR_NAV_SLIDER_RANGE = 'data-slider-range';
export const SELECTOR_FACETED_NAV_SLIDER = `[${ATTR_NAV_SLIDER_RANGE}]`;

export class CustomPriceSlideFacet extends PriceSlideFacet {
  /**
   * Build complete price slider with HTML structure and NoUI Slider initialization
   * Creates price displays, slider element, and event handlers
   */
  drawSlider() {
    // Clear any existing content in the container
    this.container.empty();

    let [ min, max ] = this.getRange();
    if(!min || min == 'min') min = 0;
    if(!max || max == 'max') max = 0;

    let rangeMin = this.getRangeMin();
    let rangeMax = this.getRangeMax();

    // Normalize range values using configuration
    const normalizedRange = SliderUtils.normalizeRange(rangeMin, rangeMax);
    rangeMin = normalizedRange.min;
    rangeMax = normalizedRange.max;

    // Create the complete UI structure
    const sliderHTML = `
      <div class="c-facets__price-slider">
        <div class="c-facets__price-display o-paragraph--1">
          <span class="c-facets__price-current" data-price-current-min>${this.formatMin(min ? min : rangeMin)}</span>
          <span class="c-facets__price-separator">-</span>
          <span class="c-facets__price-current" data-price-current-max>${this.formatMax(max ? max : rangeMax)}</span>
        </div>
        <div class="o-slider">
          <div class="o-slider__slider" data-slider-range></div>
        </div>
        <div class="o-slider__min-max o-paragraph--2">
          <div class="o-slider__min" data-price-range-min>${this.formatMin(rangeMin)}</div>
          <div class="o-slider__max" data-price-range-max>${this.formatMax(rangeMax)}</div>
        </div>
      </div>
    `;

    // Insert the HTML into the container
    this.container.html(sliderHTML);

    // Now find the slider element we just created
    let el = this.container.find(SELECTOR_FACETED_NAV_SLIDER);
    if(!el.length) return;

    if(typeof this.rangeSlider !== typeof undefined) this.rangeSlider.destroy();

    this.rangeElement = el[0];
    // Create slider configuration using the centralized config
    const sliderConfigObj = SliderUtils.createSliderConfig(
      min ? min : rangeMin,
      max ? max : rangeMax,
      rangeMin,
      rangeMax
    );
    
    this.rangeSlider = this.noUiSlider.create(this.rangeElement, sliderConfigObj);

    this.rangeElement.noUiSlider.on('update', (values) => {
      this.onSliderUpdate(values);
      this.updatePriceDisplay(values);
    });
    this.rangeElement.noUiSlider.on('change', (values) => this.onSliderChange(values));
  }

  updatePriceRangeDisplay(rangeMin, rangeMax) {
    // Update the min/max available prices (bottom labels)
    const minElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.priceRangeMin);
    const maxElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.priceRangeMax);
    
    if (minElement.length) {
      minElement.text(this.formatMin(rangeMin));
    }
    if (maxElement.length) {
      maxElement.text(this.formatMax(rangeMax));
    }
  }

  updateCurrentPriceDisplay(currentMin, currentMax) {
    // Update the current filtered prices (top display)
    const currentMinElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.priceCurrentMin);
    const currentMaxElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.priceCurrentMax);
    
    if (currentMinElement.length) {
      currentMinElement.text(this.formatMin(currentMin));
    }
    if (currentMaxElement.length) {
      currentMaxElement.text(this.formatMax(currentMax));
    }
  }

  onSliderUpdate(values) {
    // Update current price display while dragging
    const [currentMin, currentMax] = values.map(v => parseInt(v));
    this.updateCurrentPriceDisplay(currentMin, currentMax);
  }

  getRangeMin() {
    // Always return 0 as the minimum price range
    return 0;
  }

  updatePriceDisplay(values) {
    // Update the selected price display elements in our custom HTML
    const minValue = parseInt(values[0]);
    const maxValue = parseInt(values[1]);
    
    const selectedMinElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.selectedMinPrice);
    const selectedMaxElement = this.container.find(PRICE_DISPLAY_CONFIG.selectors.selectedMaxPrice);
    
    if (selectedMinElement.length) {
      selectedMinElement.text(this.formatMin(minValue));
    }
    
    if (selectedMaxElement.length) {
      selectedMaxElement.text(this.formatMax(maxValue));
    }
  }

  onSliderChange(values) {
    // Handle the actual filtering when slider change is complete
    const [currentMin, currentMax] = values.map(v => parseInt(v));
    
    // Call the parent's onSliderUpdate method to handle the filtering
    if (super.onSliderUpdate) {
      super.onSliderUpdate(values);
    }
    
    // Update the price range in the filter
    this.filter.setSettings([`${currentMin}-${currentMax}`]);
  }

  formatMax(max) {
    return SliderUtils.formatPrice(max, printMoney);
  }

  formatMin(min) {
    return SliderUtils.formatPrice(min, printMoney);
  }
}
